import { FormControl, FormLabel, Radio, RadioGroup, Tabs, Tab, Box, IconButton, Menu, MenuItem } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import Paper from '@mui/material/Paper';
import { makeStyles } from '@mui/styles';
import Switch from '@mui/material/Switch';
import { gql, useQuery } from '@apollo/client';
import { toNumber } from 'lodash';
import get from 'lodash/get';
import { useCallback } from 'react';
import { useRef } from 'react';
import React, { useState, useEffect } from 'react';
import { flushSync } from 'react-dom';
import DocumentTitle from 'react-document-title';
import { useIntl } from 'react-intl';
import { useParams, Link, useLocation, useHistory, Prompt } from 'react-router-dom';
import FormCard from '../../components/FormCard';
import FHGTextField from '../../components/TextField';
import { USERS_PATH, OPERATOR_PATH, OPERATORS_PATH } from '../../Constants';
import CheckboxFHG from '../../fhg/components/CheckboxFHG';
import useMutationFHG from '../../fhg/components/data/useMutationFHG';
import DataLoadingAndErrors from '../../fhg/components/DataLoadingAndErrors';
import ModalDialog from '../../fhg/components/dialog/ModalDialog';
import PendingDeletionBanner from '../../fhg/components/PendingDeletionBanner';
import Grid from '../../fhg/components/Grid';
import FHGTypography from '../../fhg/components/Typography';
import { handleChange as handleChangeUtil } from '../../fhg/utils/Utils';
import { formatMessage } from '../../fhg/utils/Utils';
import TestLengthTable from './TestLengthTable';
import TestTypeTable from './TestTypeTable';
import TestSystemPressureTable from './TestSystemPressuresTable';
import PressureSpecsTable from './PressureSpecsTable';
import PipeDiameterTable from './PipeDiameterTable';
import GaugeCalibrationTable from './GaugeCalibrationTable';
import GaugeDevicesTable from './GaugeDevicesTable';
import EqualizationTimeTable from './EqualizationTimeTable';
import ZoneSegmentsTable from './ZoneSegmentsTable';
import FaqTable from './FaqTable';
import ExtraFieldTable from './ExtraFieldTable';
import CopyOperatorDialog from './CopyOperatorDialog';

// Operator page tabs, in display order
const TABS = {
   settings: 'settings',
   extraFields: 'extraFields',
   equalizationTime: 'equalizationTime',
   zonesSegments: 'zonesSegments',
   devices: 'devices',
   faq: 'faq',
};

const useOperatorsStyles = makeStyles(theme => ({
   contentStyle: {
      overflow: 'auto',
      height: '100%',
      flex: '1 1',
   },
   paperStyle: {
      margin: theme.spacing(2),
      padding: theme.spacing(2),
      maxHeight: `calc(100% - ${theme.spacing(3)}px)`,
      [theme.breakpoints.down('sm')]: {
         margin: theme.spacing(1),
         maxHeight: `calc(100% - ${theme.spacing(2)}px)`,
      },
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
   },
   titleStyle: {
      marginBottom: theme.spacing(2),
   },
   titleStyleInner: {
      textOverflow: 'ellipsis',
      overflow: 'hidden',
   },
   noDataTitleStyle: {
      marginTop: theme.spacing(2),
      marginLeft: theme.spacing(2),
   },
   labelStyle: {
      marginTop: 4,
      marginLeft: 4,
      '& label': {
         fontWeight: 500,
         fontSize: 16,
         color: 'black !important',
      },
   },
   calibrationSectionStyle: { 
      marginTop: 16,
      marginBottom: 8,
      fontWeight: 500,
      fontSize: 16,
   },
   tabsContainer: {
      borderBottom: '1px solid #e0e0e0',
      marginBottom: theme.spacing(2),
   },
}), { name: 'operatorStyles' });

// Get all the operators.
export const OPERATOR_QUERY = gql`
   query getOperators {
      operators:operator_All {
         id
         name
         pendingDeletionDateTime
      }
   }
`;

// Create fragment for the operator. Using the same fragment for all Operator queries, updates the cache automatically.
const OPERATOR_FRAGMENT = gql`
   fragment operatorInfo on Operator {
      id
      name
      email:adminEmail
      sendEmail
      emailRecipient
      emailType
      gpsTimeout
      minimumAllowablePressure
      defaultStabilizationPeriod
      autoDeleteDays:autoDeleteAfterDays
      isActive
      pendingDeletionDateTime
      privacyPolicy
      sendFailedTests
      maximumAllowablePressureDrop
      maximumAllowablePressure
      prohibitLowBatTest,
      singleSegmentOnly,
      testEntireLog,
      FieldLabel01,
      FieldLabel02,
      FieldLabel03,
      FieldLabel04,
      FieldLabel05,
      FieldLabel06,
      FieldLabel07,
      FieldLabel08,
      logBeforeTestDuration,
      roundPressureValues,
      addOriginalPressureToReport,
      certifyText,
      multipleSegmentPipe,
      uploadPhotos,
      temperatureCompensation,
      equalizationTimeCalculation,
      showFaqs,
      showBugReports,
      calibrationSettings {
         id
         validityPeriod
         warningPeriod
      }
      pipeDiameters {
         id
         diameter
         operatorId
      }
   }
`;

// Get the operator with the given operator ID.
export const OPERATOR_BY_ID_QUERY = gql`
   query getOperatorById($id: Int!)
   {
      operator:operator_ById(operatorId: $id) {
         ...operatorInfo
      }
   }
   ${OPERATOR_FRAGMENT}
`;

// Create an operator with the properties.
const OPERATOR_CREATE = gql`
   mutation OperatorCreate($name: String, $email: String, $defaultStabilizationPeriod: Int, $autoDeleteDays: Int, $isActive: Boolean, $privacyPolicy: String, $sendFailedTests: Boolean, $sendEmail: Boolean, $emailRecipient: String, $emailType: String, $gpsTimeout: Int, $prohibitLowBatTest:Boolean, $testEntireLog:Boolean, $singleSegmentOnly:Boolean, $multipleSegmentPipe:Boolean, $FieldLabel01: String, $FieldLabel02: String, $FieldLabel03: String, $FieldLabel04: String, $FieldLabel05: String, $FieldLabel06: String, $FieldLabel07: String, $FieldLabel08: String, $logBeforeTestDuration: Boolean, $roundPressureValues: Boolean, $addOriginalPressureToReport: Boolean, $certifyText: String, $uploadPhotos: Boolean, $temperatureCompensation: Boolean, $showFaqs: Boolean, $showBugReports: Boolean) {
      operator: operator_Create(operator: {name: $name, adminEmail: $email, defaultStabilizationPeriod: $defaultStabilizationPeriod, autoDeleteAfterDays: $autoDeleteDays, isActive: $isActive, privacyPolicy: $privacyPolicy, sendFailedTests: $sendFailedTests,  sendEmail: $sendEmail, emailRecipient: $emailRecipient, emailType: $emailType, gpsTimeout: $gpsTimeout, prohibitLowBatTest: $prohibitLowBatTest, testEntireLog: $testEntireLog, singleSegmentOnly: $singleSegmentOnly, multipleSegmentPipe: $multipleSegmentPipe, FieldLabel01: $FieldLabel01, FieldLabel02: $FieldLabel02, FieldLabel03: $FieldLabel03, FieldLabel04: $FieldLabel04, FieldLabel05: $FieldLabel05, FieldLabel06: $FieldLabel06, FieldLabel07: $FieldLabel07, FieldLabel08: $FieldLabel08, logBeforeTestDuration: $logBeforeTestDuration, roundPressureValues: $roundPressureValues, addOriginalPressureToReport: $addOriginalPressureToReport, certifyText: $certifyText, uploadPhotos: $uploadPhotos, temperatureCompensation: $temperatureCompensation, showFaqs: $showFaqs, showBugReports: $showBugReports}) {
         ...operatorInfo
      }
   }
   ${OPERATOR_FRAGMENT}
`;

// Update an operator with the properties.
const OPERATOR_UPDATE = gql`
   mutation OperatorUpdate($operatorId: Int!, $name: String, $email: String, $defaultStabilizationPeriod: Int, $autoDeleteDays: Int, $isActive: Boolean, $privacyPolicy: String, $sendFailedTests: Boolean, $sendEmail: Boolean, $emailRecipient: String, $emailType: String, $gpsTimeout: Int, $prohibitLowBatTest:Boolean, $testEntireLog:Boolean, $singleSegmentOnly:Boolean, $multipleSegmentPipe:Boolean, $FieldLabel01: String, $FieldLabel02: String, $FieldLabel03: String, $FieldLabel04: String, $FieldLabel05: String, $FieldLabel06: String, $FieldLabel07: String, $FieldLabel08: String, $logBeforeTestDuration: Boolean, $roundPressureValues: Boolean, $addOriginalPressureToReport: Boolean, $certifyText: String, $uploadPhotos: Boolean, $temperatureCompensation: Boolean, $showFaqs: Boolean, $showBugReports: Boolean) {
      operator: operator_Update(operatorId: $operatorId, operator: {name: $name, adminEmail: $email, defaultStabilizationPeriod: $defaultStabilizationPeriod, autoDeleteAfterDays: $autoDeleteDays, isActive: $isActive, privacyPolicy: $privacyPolicy, sendFailedTests: $sendFailedTests, sendEmail: $sendEmail, emailRecipient: $emailRecipient, emailType: $emailType, gpsTimeout: $gpsTimeout, prohibitLowBatTest: $prohibitLowBatTest, testEntireLog: $testEntireLog, singleSegmentOnly: $singleSegmentOnly, multipleSegmentPipe: $multipleSegmentPipe, FieldLabel01: $FieldLabel01, FieldLabel02: $FieldLabel02, FieldLabel03: $FieldLabel03, FieldLabel04: $FieldLabel04, FieldLabel05: $FieldLabel05, FieldLabel06: $FieldLabel06, FieldLabel07: $FieldLabel07, FieldLabel08: $FieldLabel08, logBeforeTestDuration: $logBeforeTestDuration, roundPressureValues: $roundPressureValues, addOriginalPressureToReport: $addOriginalPressureToReport, certifyText: $certifyText, uploadPhotos: $uploadPhotos, temperatureCompensation: $temperatureCompensation, showFaqs: $showFaqs, showBugReports: $showBugReports}) {
         ...operatorInfo
      }
   }
   ${OPERATOR_FRAGMENT}
`;

// Scalar fields accepted by OPERATOR_CREATE - used to seed the create mutation from pre-filled defaultValues
// (e.g. a duplicated operator) for any field the user hasn't explicitly edited.
const OPERATOR_SCALAR_FIELDS = [
   'name', 'email', 'defaultStabilizationPeriod', 'autoDeleteDays', 'privacyPolicy', 'sendFailedTests',
   'sendEmail', 'emailRecipient', 'emailType', 'gpsTimeout', 'prohibitLowBatTest', 'testEntireLog',
   'singleSegmentOnly', 'multipleSegmentPipe', 'FieldLabel01', 'FieldLabel02', 'FieldLabel03', 'FieldLabel04',
   'FieldLabel05', 'FieldLabel06', 'FieldLabel07', 'FieldLabel08', 'logBeforeTestDuration', 'roundPressureValues',
   'addOriginalPressureToReport', 'certifyText', 'uploadPhotos', 'temperatureCompensation', 'showFaqs', 'showBugReports',
];

const CALIBRATION_SETTINGS_CREATE = gql`
   mutation CalibrationSettingsCreate($operatorId: Int!, $validityPeriod: Int, $warningPeriod: Int) {
      calibrationSettings: calibrationSettings_Create(calibrationSettings: {
         operatorId: $operatorId,
         validityPeriod: $validityPeriod,
         warningPeriod: $warningPeriod
      }) {
         id
         operatorId
         validityPeriod
         warningPeriod
      }
   }
`;

const CALIBRATION_SETTINGS_UPDATE = gql`
   mutation CalibrationSettingsUpdate($calibrationSettingsId: Int!, $validityPeriod: Int, $warningPeriod: Int) {
      calibrationSettings: calibrationSettings_Update(
         calibrationSettingsId: $calibrationSettingsId,
         calibrationSettings: {
            validityPeriod: $validityPeriod,
            warningPeriod: $warningPeriod
         }
      ) {
         id
         operatorId
         validityPeriod
         warningPeriod
      }
   }
`;

// Zone/segment duplication is temporarily disabled - see the commented-out logic in handleSubmit below.
// const ZONE_CREATE = gql`
//    mutation OperatorZoneCreate($name: String!, $operatorId: Int!) {
//       zone: zone_Create(zone: { name: $name, operatorId: $operatorId }) {
//          id
//       }
//    }
// `;
//
// const SEGMENT_CREATE = gql`
//    mutation OperatorSegmentCreate($operatorId: Int!, $zoneId: Int, $job: String, $serviceLineNumber: String, $lengthFT: Float, $pressureTestSegment: String, $diameter: Float) {
//       segment: segment_Create(segment: {
//          operatorId: $operatorId, zoneId: $zoneId, job: $job, serviceLineNumber: $serviceLineNumber,
//          lengthFT: $lengthFT, pressureTestSegment: $pressureTestSegment, diameter: $diameter
//       }) {
//          id
//       }
//    }
// `;

// Schedules an operator for permanent deletion after a grace period (see operator_Delete resolver).
const OPERATOR_DELETE = gql`
   mutation OperatorDelete($operatorId: Int!) {
      operator_Delete(operatorId: $operatorId)
   }
`;

// Cancels a pending scheduled deletion (see operator_UnDelete resolver).
const OPERATOR_CANCEL_DELETE = gql`
   mutation OperatorCancelDelete($operatorId: Int!) {
      operator_UnDelete(operatorId: $operatorId)
   }
`;

const OPERATOR_EQUALIZATION_FLAG_UPDATE = gql`
   mutation OperatorEqualizationFlagUpdate($operatorId: Int!, $equalizationTimeCalculation: Boolean) {
      operator: operator_Update(
         operatorId: $operatorId,
         operator: { equalizationTimeCalculation: $equalizationTimeCalculation }
      ) {
         id
         equalizationTimeCalculation
      }
   }
`;

/**
 * The Operator component to create and edit an operator.
 *
 * Reviewed: 3/26/20
 */
export default function Operator() {
   const { id } = useParams();
   const operatorId = toNumber(id);
   const location = useLocation();
   const history = useHistory();
   const intl = useIntl();
   const classes = useOperatorsStyles();
   const testLengthTableRef = useRef();
   const testTypeTableRef = useRef();
   const testSystemPressureRef = useRef();
   const pressureSpecTableRef = useRef();
   const pipeDiameterTableRef = useRef();
   const gaugeCalibrationTableRef = useRef();
   const gaugeDevicesTableRef = useRef();
   const equalizationTimeTableRef = useRef();
   const faqTableRef = useRef();
   const extraFieldTableRef = useRef();

   const isCreate = get(location, 'state.isCreate');
   const initialOperator = get(location, 'state.initialOperator');
   const [defaultValues, setDefaultValues] = useState(() => ({
      maximumAllowablePressure: 105, maximumAllowablePressureDrop: 4, showFaqs: true, showBugReports: true, ...initialOperator,
   }));
   const { data, loading, error } = useQuery(OPERATOR_BY_ID_QUERY, { variables: { id: operatorId }, skip: !operatorId });
   const [operatorCreate, { loading: createLoading, error: createError }] = useMutationFHG(OPERATOR_CREATE, undefined,
      { query: OPERATOR_QUERY });
   const [operatorUpdate, { loading: updateLoading, error: updateError }] = useMutationFHG(OPERATOR_UPDATE);
   const [calibrationSettingsCreate] = useMutationFHG(CALIBRATION_SETTINGS_CREATE);
   const [calibrationSettingsUpdate] = useMutationFHG(CALIBRATION_SETTINGS_UPDATE);
   const [operatorEqualizationFlagUpdate] = useMutationFHG(OPERATOR_EQUALIZATION_FLAG_UPDATE);
   // Zone/segment duplication is temporarily disabled - see the commented-out logic in handleSubmit below.
   // const [zoneCreate] = useMutationFHG(ZONE_CREATE);
   // const [segmentCreate] = useMutationFHG(SEGMENT_CREATE);
   const [operatorDelete, { loading: deleteLoading, error: deleteError }] = useMutationFHG(
      OPERATOR_DELETE, { refetchQueries: [{ query: OPERATOR_QUERY }] });
   const [operatorCancelDelete, { loading: cancelDeleteLoading, error: cancelDeleteError }] = useMutationFHG(
      OPERATOR_CANCEL_DELETE, { refetchQueries: [{ query: OPERATOR_QUERY }] });
   const [isActive, setActive] = useState(() => initialOperator?.isActive ?? true);
   const [isChanged, setIsChanged] = useState(false);
   const [isSaving, setIsSaving] = useState(false);
   const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
   const [deleteConfirmText, setDeleteConfirmText] = useState('');
   const [isDuplicateModalOpen, setDuplicateModalOpen] = useState(false);
   const [settingsMenuAnchor, setSettingsMenuAnchor] = useState(null);
   const [editValues, setEditValues] = useState({});
   const [selectedTab, setSelectedTab] = useState(TABS.settings);

   const getValue = useCallback((path, defaultValue) => {
      const editValue = get(editValues, path);
      return editValue !== undefined ? editValue : get(defaultValues, path) || defaultValue;
   }, [editValues, defaultValues]);

   /**
    * Toggle the active property. Changes are immediately saved.
    * @param event The checkbox event.
    */
   const toggleChecked = (event) => {
      setActive(event.target.checked);
      operatorUpdate({ variables: { operatorId, isActive: event.target.checked } });
   };

   /**
    * Set the default values and active from the existing operator.
    */
   useEffect(() => {
      if (!!data) {
         setDefaultValues({
            ...data.operator, ...{
               maximumAllowablePressure: data.operator?.maximumAllowablePressure || 105,
               maximumAllowablePressureDrop: data.operator?.maximumAllowablePressureDrop || 4,
            }
         });
         setActive(get(data, 'operator.isActive') || false);
      }
   }, [data]);

   /**
    * Handle onChange events for the inputs.
    *
    * NOTE:
    * Input components MUST have their name set to be set in the editValues.
    *
    * @param event The event that changed the input.
    */
   const handleChange = event => {
      setEditValues({
         ...editValues,
         [event.target.name]: event.target.type === 'number' ? event.target.valueAsNumber : event.target.value
      });
      setIsChanged(true);
   };

   const handleCheckboxChange = (event) => {
      const newValue = handleChangeUtil(event);
      const nextValues = { ...editValues, ...newValue };
      if (event.target.name === 'roundPressureValues' && !event.target.checked) {
         delete nextValues.addOriginalPressureToReport;
      }
      setEditValues(nextValues);
      setIsChanged(true);
   };

   const handleTestLengthChange = () => {
      setIsChanged(true);
   };

   const handleTestTypeChange = () => {
      setIsChanged(true);
   };

   const handlePressureSpecChange = () => {
      setIsChanged(true);
   };

   /**
    * Handle onSubmit for the form. Mutates the database object with the changes.
    */
   const handleSubmit = async () => {
      if (isChanged) {
         try {
            setIsSaving(true);
            let mutate = isCreate ? operatorCreate : operatorUpdate;
            // In create mode, seed from the pre-filled defaultValues (e.g. a duplicated operator) for any
            // scalar field the user hasn't explicitly edited; in edit mode, only send the touched deltas.
            const scalarVariables = isCreate
               ? OPERATOR_SCALAR_FIELDS.reduce((accumulator, field) => {
                  const value = editValues[field] !== undefined ? editValues[field] : defaultValues[field];
                  if (value !== undefined) {
                     accumulator[field] = value;
                  }
                  return accumulator;
               }, {})
               : editValues;
            const result = await mutate({ variables: { operatorId, ...scalarVariables, isActive } });
            const newOperatorId = isCreate ? result.data.operator.id : operatorId;

            // Save calibration settings if either field was touched, or pre-filled on create.
            const calibrationChanged =
               editValues.calibrationValidityPeriod !== undefined ||
               editValues.calibrationWarningPeriod !== undefined ||
               (isCreate && !!defaultValues.calibrationSettings);

            try {
               if (calibrationChanged) {
                  // On create, the operator may already have been provisioned with a calibrationSettings row by
                  // operator_Create - use the id the create mutation actually returned, not the pre-fill draft's
                  // (which never has a real id), or we'd try to insert a second row and violate the unique constraint.
                  const existingCalibrationId = isCreate
                     ? result.data.operator.calibrationSettings?.id
                     : defaultValues.calibrationSettings?.id;

                  if (existingCalibrationId) {
                     const calibrationResult = await calibrationSettingsUpdate({
                        variables: {
                           calibrationSettingsId: existingCalibrationId,
                           validityPeriod: editValues.calibrationValidityPeriod ?? defaultValues.calibrationSettings?.validityPeriod,
                           warningPeriod: editValues.calibrationWarningPeriod ?? defaultValues.calibrationSettings?.warningPeriod,
                        }
                     });
                     // Update operator with new calibration data
                     result.data.operator.calibrationSettings = calibrationResult.data.calibrationSettings;
                  } else {
                     const calibrationResult = await calibrationSettingsCreate({
                        variables: {
                           operatorId: newOperatorId,
                           validityPeriod: editValues.calibrationValidityPeriod ?? defaultValues.calibrationSettings?.validityPeriod,
                           warningPeriod: editValues.calibrationWarningPeriod ?? defaultValues.calibrationSettings?.warningPeriod,
                        }
                     });
                     // Update operator with new calibration data
                     result.data.operator.calibrationSettings = calibrationResult.data.calibrationSettings;
                  }
               }
            } catch (calibrationError) {
               console.warn('Calibration settings submission error (continuing):', calibrationError);
            }

            // Each related table is submitted independently so a failure in one (e.g. a bad id remap) can't
            // silently prevent the others - most importantly the pipe diameter and equalization tables, which
            // don't depend on test types/pressure specs at all - from being saved.
            let testTypeIdMap = {};
            let testSystemPressureIdMap = {};
            try {
               testTypeIdMap = (await testTypeTableRef.current.submit(newOperatorId)) || {};
               testSystemPressureIdMap = (await testSystemPressureRef.current.submit(newOperatorId)) || {};
            } catch (testTypeError) {
               console.warn('Test type / system pressure submission error (continuing):', testTypeError);
            }

            try {
               await pressureSpecTableRef.current.submit(newOperatorId, testTypeIdMap, testSystemPressureIdMap);
            } catch (pressureSpecError) {
               console.warn('Pressure spec submission error (continuing):', pressureSpecError);
            }

            try {
               await testLengthTableRef.current.submit(newOperatorId, testTypeIdMap);
            } catch (testLengthError) {
               console.warn('Test duration submission error (continuing):', testLengthError);
            }

            try {
               if (pipeDiameterTableRef.current) {
                  await pipeDiameterTableRef.current.submit(newOperatorId);
               }
            } catch (pipeDiameterError) {
               console.warn('Pipe diameter submission error (continuing):', pipeDiameterError);
            }

            // Zone/segment duplication is temporarily disabled.
            // if (isCreate && initialOperator?.zones?.length > 0) {
            //    try {
            //       const zoneIdMap = {};
            //       const zoneResults = await Promise.all(initialOperator.zones.map(zone =>
            //          zoneCreate({ variables: { name: zone.name, operatorId: newOperatorId } })));
            //       zoneResults.forEach((zoneResult, index) => {
            //          zoneIdMap[initialOperator.zones[index].id] = zoneResult.data.zone.id;
            //       });
            //
            //       await Promise.all((initialOperator.segments || []).map(segment => segmentCreate({
            //          variables: {
            //             operatorId: newOperatorId,
            //             zoneId: segment.zoneId ? zoneIdMap[segment.zoneId] : undefined,
            //             job: segment.job,
            //             serviceLineNumber: segment.serviceLineNumber,
            //             lengthFT: segment.lengthFT,
            //             pressureTestSegment: segment.pressureTestSegment,
            //             diameter: segment.diameter,
            //          },
            //       })));
            //    } catch (zoneSegmentError) {
            //       console.warn('Zone/segment submission error (continuing):', zoneSegmentError);
            //    }
            // }

            if (isCreate) {
               try {
                  const equalizationTimeCalculationValue = editValues.equalizationTimeCalculation !== undefined
                     ? editValues.equalizationTimeCalculation
                     : defaultValues.equalizationTimeCalculation;
                  if (equalizationTimeCalculationValue !== undefined) {
                     const flagResult = await operatorEqualizationFlagUpdate({
                        variables: { operatorId: newOperatorId, equalizationTimeCalculation: equalizationTimeCalculationValue },
                     });
                     result.data.operator.equalizationTimeCalculation = flagResult.data.operator.equalizationTimeCalculation;
                  }
               } catch (equalizationFlagError) {
                  console.warn('Equalization time flag submission error (continuing):', equalizationFlagError);
               }

               try {
                  if (equalizationTimeTableRef.current) {
                     await equalizationTimeTableRef.current.submit(newOperatorId);
                  }
               } catch (equalizationError) {
                  console.warn('Equalization time table submission error (continuing):', equalizationError);
               }
            }

            // Reset defaultValues to trigger UI update with fresh data, including calibration settings
            setDefaultValues(result.data.operator);
            setIsSaving(false);

            // Flush isChanged/editValues synchronously before navigating so the unsaved-changes Prompt (which
            // reads the latest committed state) doesn't intercept this same, already-successful, save.
            flushSync(() => {
               setIsChanged(false);
               setEditValues({});
            });

            if (isCreate) {
               history.replace(OPERATOR_PATH.replace(':id?', newOperatorId));
            }
         } catch (e) {
            console.error('Error saving operator:', e);
            setIsChanged(false);
            setEditValues({});
            setIsSaving(false);
         }
      }
   }; 

   /**
    * Cancel the changes to the operator.
    */
   const handleCancel = () => {
      setIsChanged(false);
      setEditValues({});
      // Wait until isChanged takes affect to avoid Prompt.
      setTimeout(() => {
         history.replace(OPERATORS_PATH, { isCreate: false });
      }, 0);
   };

   /**
    * Open the operator settings menu (duplicate/delete).
    */
   const handleSettingsMenuOpen = (event) => {
      setSettingsMenuAnchor(event.currentTarget);
   };

   /**
    * Close the operator settings menu.
    */
   const handleSettingsMenuClose = () => {
      setSettingsMenuAnchor(null);
   };

   /**
    * Close the settings menu and open the duplicate operator dialog.
    */
   const handleDuplicateMenuItemClick = () => {
      setSettingsMenuAnchor(null);
      setDuplicateModalOpen(true);
   };

   /**
    * Close the settings menu and open the delete confirmation modal.
    */
   const handleDeleteMenuItemClick = () => {
      setSettingsMenuAnchor(null);
      setDeleteModalOpen(true);
   };

   /**
    * Close the delete confirmation modal without deleting.
    */
   const handleDeleteModalClose = () => {
      setDeleteModalOpen(false);
      setDeleteConfirmText('');
   };

   /**
    * Schedule the operator for permanent deletion after the grace period, once the name has been confirmed.
    */
   const handleDeleteConfirm = async (event) => {
      event && event.preventDefault();
      try {
         await operatorDelete({ variables: { operatorId } });
         setDeleteModalOpen(false);
         setDeleteConfirmText('');
      } catch (e) {
         console.error('Error scheduling operator deletion:', e);
      }
   };

   /**
    * Cancel a pending scheduled deletion for this operator.
    */
   const handleCancelDeletion = async () => {
      try {
         await operatorCancelDelete({ variables: { operatorId } });
      } catch (e) {
         console.error('Error cancelling operator deletion:', e);
      }
   };

   /**
    * Save the equalization time calculation flag and table rows.
    */
   const handleEqualizationSubmit = async () => {
      setIsSaving(true);
      try {
         if (editValues.equalizationTimeCalculation !== undefined) {
            const result = await operatorEqualizationFlagUpdate({
               variables: { operatorId, equalizationTimeCalculation: editValues.equalizationTimeCalculation }
            });
            setDefaultValues(previous => ({ ...previous, equalizationTimeCalculation: result.data.operator.equalizationTimeCalculation }));
         }
         if (equalizationTimeTableRef.current) {
            await equalizationTimeTableRef.current.submit();
         }
         setEditValues(previous => {
            const updated = { ...previous };
            delete updated.equalizationTimeCalculation;
            return updated;
         });
      } catch (e) {
         console.error('Error saving equalization time calculation:', e);
      } finally {
         setIsSaving(false);
      }
   };

   /**
    * Cancel the changes to the equalization time calculation tab.
    */
   const handleEqualizationCancel = () => {
      setEditValues(previous => {
         const updated = { ...previous };
         delete updated.equalizationTimeCalculation;
         return updated;
      });
      if (equalizationTimeTableRef.current) {
         equalizationTimeTableRef.current.refetch();
      }
   };

   /**
    * Handle tab change.
    */
   const handleTabChange = (event, newValue) => {
      setSelectedTab(newValue);
      // Refetch extra fields when switching to that tab
      if (newValue === TABS.extraFields && extraFieldTableRef.current) {
         extraFieldTableRef.current.refetch();
      }
      // Refetch equalization times when switching to that tab
      if (newValue === TABS.equalizationTime && equalizationTimeTableRef.current) {
         equalizationTimeTableRef.current.refetch();
      }
      // Refetch devices and gauge calibrations when switching to that tab
      if (newValue === TABS.devices) {
         gaugeDevicesTableRef.current && gaugeDevicesTableRef.current.refetch();
         gaugeCalibrationTableRef.current && gaugeCalibrationTableRef.current.refetch();
      }
      // Refetch FAQ entries when switching to that tab
      if (newValue === TABS.faq && faqTableRef.current) {
         faqTableRef.current.refetch();
      }
   };

   // If an operator is specified...
   if (!!id) {
      // Show loading progress and errors.
      if (error) {
         console.log("Operator show error 1 " + error + " " + loading);
         return <DataLoadingAndErrors error={error} isLoading={loading} />
      } else if (!error && !(get(data, 'operator') || undefined)) {
         return (
            <FHGTypography className={classes.noDataTitleStyle} variant={'h5'} noWrap id={'operator.noOperator.title'} />
         )
      }
      // No operator was selected and they aren't creating one.
   } else if (!id && !isCreate) {
      return (
         <FHGTypography className={classes.noDataTitleStyle} variant={'h5'} noWrap id={'operator.noOperator.label'} />
      )
   }

   return (
      <DocumentTitle title={defaultValues.name || ''}>
         <Grid item className={classes.contentStyle}>
            <Prompt when={isChanged} message={() => formatMessage(intl, 'leavePage', 'Discard changes?')} />
            <DataLoadingAndErrors error={error || createError || updateError || deleteError || cancelDeleteError}
               isLoading={loading || createLoading || updateLoading || isSaving} />
            <Paper square className={classes.paperStyle}> 
               {/* Header */}
               <Grid item container className={classes.titleStyle} fullWidth={true} spacing={2} resizable={false}>
                  <Grid item flex={'2000 2000'} style={{ minWidth: 'min-content', maxWidth: 'fit-content' }}>
                     <FHGTypography variant={'h5'} className={classes.titleStyleInner}
                        id={!defaultValues.name ? 'operator.new.title' : undefined}>
                        {defaultValues.name}
                     </FHGTypography>
                  </Grid>
                  <Grid item resizable={false} style={{ minWidth: 'fit-content' }}>
                     <FormControlLabel
                        control={<Switch checked={isActive} onChange={toggleChecked} color={'primary'} />}
                        label={<FHGTypography variant={'h5'} className={classes.titleStyleInner} id={'operator.active.label'} />}
                        style={{ marginRight: 0 }}
                     />
                  </Grid>
                  <div style={{ padding: 0, flex: '1 1' }} />
                  <Grid item resizable={false}>
                     <Button component={Link} to={USERS_PATH.replace(':operatorId', id)} color='secondary'>
                        <FHGTypography noWrap id={'operator.openOperatorAdmin.title'} />
                     </Button>
                  </Grid>
                  {!isCreate && !!operatorId && (
                     <Grid item resizable={false}>
                        <IconButton aria-label='operator settings' size='small' onClick={handleSettingsMenuOpen}>
                           <MoreVertIcon />
                        </IconButton>
                        <Menu anchorEl={settingsMenuAnchor} open={!!settingsMenuAnchor} onClose={handleSettingsMenuClose}>
                           <MenuItem onClick={handleDuplicateMenuItemClick}>
                              <FHGTypography noWrap id={'operator.duplicateOperator.button'} />
                           </MenuItem>
                           {defaultValues.pendingDeletionDateTime ? (
                              <MenuItem onClick={() => { setSettingsMenuAnchor(null); handleCancelDeletion(); }} sx={{ color: 'error.main' }}>
                                 <FHGTypography noWrap id={'operator.cancelDeletion.button'} />
                              </MenuItem>
                           ) : (
                              <MenuItem onClick={handleDeleteMenuItemClick} sx={{ color: 'error.main' }}>
                                 <FHGTypography noWrap id={'operator.deleteOperator.button'} />
                              </MenuItem>
                           )}
                        </Menu>
                     </Grid>
                  )}
               </Grid>

               {!isCreate && !!operatorId && (
                  <PendingDeletionBanner
                     pendingDeletionDateTime={defaultValues.pendingDeletionDateTime}
                     onCancel={handleCancelDeletion}
                     isCancelling={cancelDeleteLoading}
                     messageKey={'operator.pendingDeletion.banner.message'}
                  />
               )}

               {isDuplicateModalOpen && (
                  <CopyOperatorDialog
                     open={true}
                     sourceOperator={defaultValues}
                     sourceOperatorId={operatorId}
                     onClose={() => setDuplicateModalOpen(false)}
                  />
               )}

               {isDeleteModalOpen && (
                  <ModalDialog
                     open={true}
                     titleKey={'operator.delete.title'}
                     titleValues={{ name: defaultValues.name }}
                     messageKey={'operator.delete.message'}
                     messageValues={{ name: defaultValues.name }}
                     submitKey={'delete.button'}
                     submitColor={'error'}
                     onSubmit={handleDeleteConfirm}
                     onClose={handleDeleteModalClose}
                     isSaving={deleteLoading}
                     isEnabled={deleteConfirmText.trim() === defaultValues.name}
                     maxWidth={'sm'}
                     fullWidth
                  >
                     <FHGTextField
                        name='deleteConfirmText'
                        label={formatMessage(intl, 'operator.delete.confirmField.label', 'Operator name')}
                        autoFocus
                        onChange={(event) => setDeleteConfirmText(event.target.value)}
                        value={deleteConfirmText}
                        fullWidth
                     />
                  </ModalDialog>
               )}

               {/* Tabs */}
               <Box className={classes.tabsContainer}>
                  <Tabs
                     value={selectedTab}
                     onChange={handleTabChange}
                     aria-label="operator tabs"
                     sx={{ borderBottom: '1px solid #e0e0e0' }}
                  >
                     <Tab label="Settings" value={TABS.settings} />
                     {operatorId && <Tab label="Extra Fields" value={TABS.extraFields} />}
                     {operatorId && <Tab label="Equalization Time" value={TABS.equalizationTime} />}
                     {operatorId && <Tab label="Zones & Segments" value={TABS.zonesSegments} />}
                     {operatorId && <Tab label="Devices" value={TABS.devices} />}
                     {operatorId && getValue('showFaqs') && <Tab label="FAQ" value={TABS.faq} />}
                  </Tabs>
               </Box>

               {/* Tab: Settings */}
               {selectedTab === TABS.settings && (
                  <FormCard onSubmit={handleSubmit} onCancel={handleCancel} disabled={isSaving}>
                  <Grid container direction={'row'} resizable overflow={'auto'} key={"mainContainer"} style={{ flex: 1, overflowY: 'auto' }}>

                     <Grid container>
                        {/* Column 1 */}
                        <Grid item xs={12} sm={6} lg={6} key={"column1"} style={{ paddingRight: '20px' }}>

                           {/* Operator name */}
                           <FHGTextField
                              key={defaultValues.name}
                              name={'name'}
                              label={<FHGTypography variant='inherit' id={'operator.name.label'} sx={{ display: 'inline' }} />}
                              defaultValue={defaultValues.name}
                              value={editValues.name}
                              onChange={handleChange}
                              disabled={isSaving}
                              required
                              fullWidth
                           />
                           {/* Operator Email */}
                           <FHGTextField
                              key={defaultValues.email}
                              name={'email'}
                              type={'email'}
                              label={<FHGTypography variant='inherit' id={'operator.email.label'} sx={{ display: 'inline' }} />}
                              defaultValue={defaultValues.email}
                              value={editValues.email}
                              onChange={handleChange}
                              disabled={isSaving}
                              required
                              fullWidth
                           />
                           {/* Operator Default Stabilization Period */}
                           <FHGTextField
                              key={defaultValues.defaultStabilizationPeriod}
                              name={'defaultStabilizationPeriod'}
                              type={'number'}
                              label={<FHGTypography variant='inherit' id={'operator.defaultStabilizationPeriod.label'} noWrap sx={{ display: 'inline' }} />}
                              defaultValue={defaultValues.defaultStabilizationPeriod}
                              value={editValues.defaultStabilizationPeriod}
                              onChange={handleChange}
                              disabled={isSaving}
                              required
                              fullWidth
                           />
                           {/* Auto Delete Days */}
                           <FHGTextField
                              key={defaultValues.autoDeleteDays}
                              name={'autoDeleteDays'}
                              type={'number'}
                              label={<FHGTypography variant='inherit' id={'operator.autoDeleteDays.label'} noWrap sx={{ display: 'inline' }} />}
                              defaultValue={defaultValues.autoDeleteDays}
                              value={editValues.autoDeleteDays}
                              onChange={handleChange}
                              disabled={isSaving}
                              required
                              fullWidth
                           />
                           {/* GPS Timeout */}
                           <FHGTextField
                              key={defaultValues.gpsTimeout}
                              name={'gpsTimeout'}
                              type={'number'}
                              label={<FHGTypography variant='inherit' id={'operator.gpsTimeout.label'} noWrap sx={{ display: 'inline' }} />}
                              defaultValue={defaultValues.gpsTimeout}
                              value={editValues.gpsTimeout}
                              onChange={handleChange}
                              disabled={isSaving}
                              required
                              fullWidth
                           />

                           {/* Calibration Settings */}
                           <Divider style={{ marginTop: 16, marginBottom: 8 }} />
                           <FHGTypography className={classes.calibrationSectionStyle}>
                              Gauge Calibration
                           </FHGTypography>
                           {/* Calibration Validity Period */}
                           <FHGTextField
                              key={defaultValues.calibrationSettings?.validityPeriod}
                              name={'calibrationValidityPeriod'}
                              type={'number'}
                              label={<FHGTypography variant='inherit' id={'operator.calibrationValidityPeriod.label'} noWrap sx={{ display: 'inline' }} />}
                              defaultValue={defaultValues.calibrationSettings?.validityPeriod ?? ''}
                              value={editValues.calibrationValidityPeriod}
                              onChange={handleChange}
                              disabled={isSaving}
                              fullWidth
                           />
                           {/* Calibration Warning Period */}
                           <FHGTextField
                              key={defaultValues.calibrationSettings?.warningPeriod}
                              name={'calibrationWarningPeriod'}
                              type={'number'}
                              label={<FHGTypography variant='inherit' id={'operator.calibrationWarningPeriod.label'} noWrap sx={{ display: 'inline' }} />}
                              defaultValue={defaultValues.calibrationSettings?.warningPeriod ?? ''}
                              value={editValues.calibrationWarningPeriod}
                              onChange={handleChange}
                              disabled={isSaving}
                              fullWidth
                           />

                        </Grid>
                        {/* Column 2 */}
                        <Grid item xs={12} sm={6} lg={6} key={"column2"}>

                           {/* FieldLabel01 */}
                           <FHGTextField
                              key={defaultValues.FieldLabel01}
                              name={'FieldLabel01'}
                              label={<FHGTypography variant='inherit' id={'operator.FieldLabel01.label'} sx={{ display: 'inline' }} />}
                              defaultValue={defaultValues.FieldLabel01}
                              value={editValues.FieldLabel01}
                              onChange={handleChange}
                              disabled={isSaving}

                              required
                              fullWidth
                           />
                           {/* FieldLabel02 */}
                           <FHGTextField
                              key={defaultValues.FieldLabel02}
                              name={'FieldLabel02'}
                              label={<FHGTypography variant='inherit' id={'operator.FieldLabel02.label'} sx={{ display: 'inline' }} />}
                              defaultValue={defaultValues.FieldLabel02}
                              value={editValues.FieldLabel02}
                              onChange={handleChange}
                              disabled={isSaving}

                              required
                              fullWidth
                           />
                           {/* FieldLabel03 */}
                           <FHGTextField
                              key={defaultValues.FieldLabel03}
                              name={'FieldLabel03'}
                              label={<FHGTypography variant='inherit' id={'operator.FieldLabel03.label'} sx={{ display: 'inline' }} />}
                              defaultValue={defaultValues.FieldLabel03}
                              value={editValues.FieldLabel03}
                              onChange={handleChange}
                              disabled={isSaving}

                              fullWidth
                           />
                           {/* FieldLabel04 */}
                           <FHGTextField
                              key={defaultValues.FieldLabel04}
                              name={'FieldLabel04'}
                              label={<FHGTypography variant='inherit' id={'operator.FieldLabel04.label'} sx={{ display: 'inline' }} />}
                              defaultValue={defaultValues.FieldLabel04}
                              value={editValues.FieldLabel04}
                              onChange={handleChange}
                              disabled={isSaving}

                              fullWidth
                           />
                           {/* FieldLabel05 */}
                           <FHGTextField
                              key={defaultValues.FieldLabel05}
                              name={'FieldLabel05'}
                              label={<FHGTypography variant='inherit' id={'operator.FieldLabel05.label'} sx={{ display: 'inline' }} />}
                              defaultValue={defaultValues.FieldLabel05}
                              value={editValues.FieldLabel05}
                              onChange={handleChange}
                              disabled={isSaving}

                              fullWidth
                           />
                           {/* FieldLabel06 */}
                           <FHGTextField
                              key={defaultValues.FieldLabel06}
                              name={'FieldLabel06'}
                              label={<FHGTypography variant='inherit' id={'operator.FieldLabel06.label'} sx={{ display: 'inline' }} />}
                              defaultValue={defaultValues.FieldLabel06}
                              value={editValues.FieldLabel06}
                              onChange={handleChange}
                              disabled={isSaving}

                              required
                              fullWidth
                           />
                           {/* FieldLabel07 */}
                           <FHGTextField
                              key={defaultValues.FieldLabel07}
                              name={'FieldLabel07'}
                              label={<FHGTypography variant='inherit' id={'operator.FieldLabel07.label'} sx={{ display: 'inline' }} />}
                              defaultValue={defaultValues.FieldLabel07}
                              value={editValues.FieldLabel07}
                              onChange={handleChange}
                              disabled={isSaving}

                              fullWidth
                           />
                           {/* FieldLabel08 */}
                           <FHGTextField
                              key={defaultValues.FieldLabel08}
                              name={'FieldLabel08'}
                              label={<FHGTypography variant='inherit' id={'operator.FieldLabel08.label'} sx={{ display: 'inline' }} />}
                              defaultValue={defaultValues.FieldLabel08}
                              value={editValues.FieldLabel08}
                              onChange={handleChange}
                              disabled={isSaving}

                              fullWidth
                           />
                        </Grid>
                     </Grid>

                     <Grid container>

                        <Grid container key={"checkboxes1Grid"}>
                           <Grid item xs={12} sm={4} lg={4} key={"checkboxes1Grid1"}>
                              <CheckboxFHG
                                 key={'prohibitLowBatTest'}
                                 name={'prohibitLowBatTest'}
                                 onChange={handleCheckboxChange}
                                 color={'default'}
                                 labelKey={'operator.prohibitLowBatTest.label'}
                                 value={'prohibitLowBatTest'}
                                 defaultChecked={defaultValues.prohibitLowBatTest}
                                 checked={editValues.prohibitLowBatTest}
                                 disabled={isSaving}
                                 marginTop={3}
                                 marginLeft={.25}
                                 fullWidth
                              />
                           </Grid>
                           <Grid item xs={12} sm={4} lg={4} key={"checkboxes1Grid2"}>
                              <CheckboxFHG
                                 key={'singleSegmentOnly'}
                                 name={'singleSegmentOnly'}
                                 onChange={handleCheckboxChange}
                                 color={'default'}
                                 labelKey={'operator.singleSegmentOnly.label'}
                                 value={'singleSegmentOnly'}
                                 defaultChecked={defaultValues.singleSegmentOnly}
                                 checked={editValues.singleSegmentOnly}
                                 disabled={isSaving}
                                 marginTop={3}
                                 marginLeft={.25}
                                 fullWidth
                              />
                           </Grid>
                           <Grid item xs={12} sm={4} lg={4} key={"checkboxes1Grid3"}>
                              <CheckboxFHG
                                 key={'testEntireLog'}
                                 name={'testEntireLog'}
                                 onChange={handleCheckboxChange}
                                 color={'default'}
                                 labelKey={'operator.testEntireLog.label'}
                                 value={'testEntireLog'}
                                 defaultChecked={defaultValues.testEntireLog}
                                 checked={editValues.testEntireLog}
                                 disabled={isSaving}
                                 marginTop={3}
                                 marginLeft={.25}
                                 fullWidth
                              />
                           </Grid>
                        </Grid>



                        <Grid container key={"checkboxes2Grid"}>
                           <Grid item xs={12} sm={4} lg={4} key={"checkboxes2Grid1"}>
                              <CheckboxFHG
                                 key={'sendFailedTests'}
                                 name={'sendFailedTests'}
                                 onChange={handleCheckboxChange}
                                 color={'default'}
                                 labelKey={'operator.sendFailedTests.label'}
                                 value={'sendFailedTests'}
                                 defaultChecked={defaultValues.sendFailedTests}
                                 checked={editValues.sendFailedTests}
                                 disabled={isSaving}
                                 marginTop={3}
                                 marginLeft={.25}
                                 fullWidth
                              />
                           </Grid>
                           <Grid item xs={12} sm={4} lg={4} key={"checkboxes2Grid2"}>
                              <CheckboxFHG
                                 key={'sendEmail'}
                                 name={'sendEmail'}
                                 onChange={handleCheckboxChange}
                                 color={'default'}
                                 labelKey={'operator.sendEmail.label'}
                                 value={'sendEmail'}
                                 defaultChecked={defaultValues.sendEmail}
                                 checked={editValues.sendEmail}
                                 disabled={isSaving}
                                 marginTop={3}
                                 marginLeft={.25}
                                 fullWidth
                              />
                           </Grid>
                           <Grid item xs={12} sm={4} lg={4} key={"checkboxes2Grid3"}>
                              <CheckboxFHG
                                 key={'logBeforeTestDuration'}
                                 name={'logBeforeTestDuration'}
                                 onChange={handleCheckboxChange}
                                 color={'default'}
                                 labelKey={'operator.logBeforeTestDuration.label'}
                                 value={'logBeforeTestDuration'}
                                 defaultChecked={defaultValues.logBeforeTestDuration}
                                 checked={editValues.logBeforeTestDuration}
                                 disabled={isSaving}
                                 marginTop={3}
                                 marginLeft={.25}
                                 fullWidth
                              />
                           </Grid>
                        </Grid>

                        <Grid container key={"checkboxes3Grid"}>
                           <Grid item xs={12} sm={4} lg={4} key={"checkboxes3Grid1"}>
                              <CheckboxFHG
                                 key={'roundPressureValues'}
                                 name={'roundPressureValues'}
                                 onChange={handleCheckboxChange}
                                 color={'default'}
                                 labelKey={'operator.testRoundPressureValues.label'}
                                 value={'roundPressureValues'}
                                 defaultChecked={defaultValues.roundPressureValues}
                                 checked={editValues.roundPressureValues}
                                 disabled={isSaving}
                                 marginTop={3}
                                 marginLeft={.25}
                                 fullWidth
                              />
                           </Grid>
                           {getValue('roundPressureValues') && (
                              <Grid item xs={12} sm={4} lg={4} key={"checkboxes3GridOriginalPressure"}>
                                 <CheckboxFHG
                                    key={'addOriginalPressureToReport'}
                                    name={'addOriginalPressureToReport'}
                                    onChange={handleCheckboxChange}
                                    color={'default'}
                                    labelKey={'operator.addOriginalPressureToReport.label'}
                                    value={'addOriginalPressureToReport'}
                                    defaultChecked={defaultValues.addOriginalPressureToReport}
                                    checked={editValues.addOriginalPressureToReport}
                                    disabled={isSaving}
                                    marginTop={3}
                                    marginLeft={.25}
                                    fullWidth
                                 />
                              </Grid>
                           )}
                           <Grid item xs={12} sm={4} lg={4} key={"checkboxes3Grid2"}>
                              <CheckboxFHG
                                 key={'uploadPhotos'}
                                 name={'uploadPhotos'}
                                 onChange={handleCheckboxChange}
                                 color={'default'}
                                 labelKey={'operator.uploadPhotos.label'}
                                 value={'uploadPhotos'}
                                 defaultChecked={defaultValues.uploadPhotos}
                                 checked={editValues.uploadPhotos}
                                 disabled={isSaving}
                                 marginTop={3}
                                 marginLeft={.25}
                                 fullWidth
                              />
                           </Grid>
                           <Grid item xs={12} sm={4} lg={4} key={"checkboxes3Grid3"}>
                              <CheckboxFHG
                                 key={'temperatureCompensation'}
                                 name={'temperatureCompensation'}
                                 onChange={handleCheckboxChange}
                                 color={'default'}
                                 labelKey={'operator.temperatureCompensation.label'}
                                 value={'temperatureCompensation'}
                                 defaultChecked={defaultValues.temperatureCompensation}
                                 checked={editValues.temperatureCompensation}
                                 disabled={isSaving}
                                 marginTop={3}
                                 marginLeft={.25}
                                 fullWidth
                              />
                           </Grid>
                           <Grid item xs={12} sm={4} lg={4} key={"checkboxes3Grid4"}>
                              <CheckboxFHG
                                 key={'showFaqs'}
                                 name={'showFaqs'}
                                 onChange={handleCheckboxChange}
                                 color={'default'}
                                 labelKey={'operator.showFaqs.label'}
                                 value={'showFaqs'}
                                 defaultChecked={defaultValues.showFaqs}
                                 checked={editValues.showFaqs}
                                 disabled={isSaving}
                                 marginTop={3}
                                 marginLeft={.25}
                                 fullWidth
                              />
                           </Grid>
                           <Grid item xs={12} sm={4} lg={4} key={"checkboxes3Grid5"}>
                              <CheckboxFHG
                                 key={'showBugReports'}
                                 name={'showBugReports'}
                                 onChange={handleCheckboxChange}
                                 color={'default'}
                                 labelKey={'operator.showBugReports.label'}
                                 value={'showBugReports'}
                                 defaultChecked={defaultValues.showBugReports}
                                 checked={editValues.showBugReports}
                                 disabled={isSaving}
                                 marginTop={3}
                                 marginLeft={.25}
                                 fullWidth
                              />
                           </Grid>
                        </Grid>

                        <Grid container direction={'row'} key={"sendEmailGrid"} style={{ marginTop: '20px' }}>
                           {getValue('sendEmail') && (
                              <>
                                 <Grid item xs={12} sm={4} lg={4} key={"sendEmailGri1"}>
                                    <FHGTextField
                                       key={defaultValues.emailRecipient}
                                       name={'emailRecipient'}
                                       label={<FHGTypography variant='inherit' id={'operator.emailRecipient.label'} noWrap sx={{ display: 'inline' }} />}
                                       defaultValue={defaultValues.emailRecipient}
                                       value={editValues.emailRecipient}
                                       onChange={handleChange}
                                       disabled={isSaving}
                                       required
                                       fullWidth
                                    />
                                 </Grid>
                                 <Grid item xs={12} sm={6} lg={6} key={"sendEmailGrid2"}>
                                    <FormControl component='fieldset' className={classes.labelStyle}>
                                       <FormLabel component='label'>Email Type</FormLabel>
                                       <RadioGroup
                                          aria-label='emailType'
                                          row
                                          name='emailType'
                                          value={getValue('emailType')}
                                          onChange={handleChange}
                                          style={{ marginLeft: 8 }}
                                       >
                                          <FormControlLabel
                                             value='pdf'
                                             control={<Radio color={'default'} />}
                                             style={{ color: 'rgb(133, 133, 133)' }}
                                             label='PDF'
                                          />
                                          <FormControlLabel
                                             value='csv'
                                             control={<Radio color={'default'} />}
                                             style={{ color: 'rgb(133, 133, 133)' }}
                                             label='CSV'
                                          />
                                          <FormControlLabel
                                             value='both'
                                             control={<Radio color={'default'} />}
                                             style={{ color: 'rgb(133, 133, 133)' }}
                                             label='PDF & CSV'
                                          />
                                       </RadioGroup>
                                    </FormControl>
                                 </Grid>
                              </>
                           )}
                           <Grid item xs={12} key={"sendEmailGrid3"} style={{ marginTop: '20px', marginBottom: '20px' }}>
                              <FHGTextField
                                 key={defaultValues.privacyPolicy}
                                 name={'privacyPolicy'}
                                 label={<FHGTypography variant='inherit' id={'operator.privacyPolicy.label'} noWrap sx={{ display: 'inline' }} />}
                                 defaultValue={defaultValues.privacyPolicy}
                                 value={editValues.privacyPolicy}
                                 onChange={handleChange}
                                 disabled={isSaving}
                                 multiline
                                 maxRows={8}
                                 fullWidth
                              />
                              <FHGTextField
                                 key={defaultValues.certifyText}
                                 name={'certifyText'}
                                 label={<FHGTypography variant='inherit' id={'operator.certifyText.label'} noWrap sx={{ display: 'inline' }} />}
                                 defaultValue={defaultValues.certifyText}
                                 value={editValues.certifyText}
                                 onChange={handleChange}
                                 disabled={isSaving}
                                 multiline
                                 maxRows={8}
                                 fullWidth
                              />
                           </Grid>

                           <Grid item xs={12} lg={6} resizable={false} key={"testTypeTable"} style={{ marginTop: 30 }}>
                              <TestTypeTable ref={testTypeTableRef} operatorId={operatorId}
                                 initialItems={initialOperator?.testTypes} onChange={handleTestTypeChange} />
                           </Grid>

                           <Grid item xs={12} lg={6} resizable={false} key={"systemPressuresTable"} style={{ marginTop: 30 }}>
                              <TestSystemPressureTable ref={testSystemPressureRef} operatorId={operatorId}
                                 initialItems={initialOperator?.testSystemPressures} onChange={handlePressureSpecChange} />
                           </Grid>

                           <Grid item xs={12} resizable={false} key={"pressureSpecsTable"} style={{ marginTop: 40, marginBottom: 60 }}>
                              <PressureSpecsTable ref={pressureSpecTableRef} operatorId={operatorId}
                                 initialItems={initialOperator?.pressureSpecs} onChange={handleTestTypeChange} />
                           </Grid>

                           <Grid item xs={12} resizable={false} key={"testLengthTable"} style={{ marginTop: 40, marginBottom: 60 }}>
                              <TestLengthTable ref={testLengthTableRef} operatorId={operatorId}
                                 initialItems={initialOperator?.testParameters} onChange={handleTestLengthChange} />
                           </Grid>

                           <Grid item xs={12} resizable={false} key={"pipeDiameterSection"} style={{ marginTop: 40, marginBottom: 60 }}>
                              <Grid container spacing={2}>
                                 <Grid item xs={12}>
                                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
                                       <CheckboxFHG
                                          key={'multipleSegmentPipe'}
                                          name={'multipleSegmentPipe'}
                                          onChange={handleCheckboxChange}
                                          color={'default'}
                                          labelKey={'operator.multipleSegmentPipe.label'}
                                          value={'multipleSegmentPipe'}
                                          defaultChecked={defaultValues.multipleSegmentPipe}
                                          checked={editValues.multipleSegmentPipe}
                                          disabled={isSaving}
                                          marginTop={0}
                                          marginLeft={0}
                                          fullWidth={false}
                                       />
                                    </div>
                                 </Grid>
                                 {getValue('multipleSegmentPipe') && (
                                    <Grid item xs={12}>
                                       <PipeDiameterTable ref={pipeDiameterTableRef} operatorId={operatorId}
                                          initialItems={initialOperator?.pipeDiameters} onChange={handleTestTypeChange} />
                                    </Grid>
                                 )}
                              </Grid>
                           </Grid>

                           {/* Equalization Time Calculation is normally its own tab (only reachable once an
                               operator exists), but while creating one it's shown inline here so it can be
                               pre-filled and saved atomically with the rest of the new operator. */}
                           {isCreate && (
                              <Grid item xs={12} resizable={false} key={"equalizationTimeSection"} style={{ marginTop: 40, marginBottom: 60 }}>
                                 <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                       <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
                                          <CheckboxFHG
                                             key={'equalizationTimeCalculation'}
                                             name={'equalizationTimeCalculation'}
                                             onChange={handleCheckboxChange}
                                             color={'default'}
                                             labelKey={'operator.equalizationTimeCalculation.label'}
                                             value={'equalizationTimeCalculation'}
                                             defaultChecked={defaultValues.equalizationTimeCalculation}
                                             checked={editValues.equalizationTimeCalculation}
                                             disabled={isSaving}
                                             marginTop={0}
                                             marginLeft={0}
                                             fullWidth={false}
                                          />
                                       </div>
                                    </Grid>
                                    {getValue('equalizationTimeCalculation') && (
                                       <Grid item xs={12}>
                                          <EqualizationTimeTable ref={equalizationTimeTableRef} operatorId={operatorId}
                                             initialItems={initialOperator?.equalizationTimes} onChange={() => setIsChanged(true)} />
                                       </Grid>
                                    )}
                                 </Grid>
                              </Grid>
                           )}
                        </Grid>
                     </Grid>
                  </Grid>
                  </FormCard>
               )}

               {/* Tab: Extra Fields */}
               {selectedTab === TABS.extraFields && operatorId && (
                  <Box sx={{ p: 2, flex: 1, overflowY: 'auto' }}>
                     <ExtraFieldTable ref={extraFieldTableRef} operatorId={operatorId} />
                  </Box>
               )}

               {/* Tab: Equalization Time */}
               {selectedTab === TABS.equalizationTime && operatorId && (
                  <FormCard onSubmit={handleEqualizationSubmit} onCancel={handleEqualizationCancel} disabled={isSaving}>
                     <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
                        <CheckboxFHG
                           key={'equalizationTimeCalculation'}
                           name={'equalizationTimeCalculation'}
                           onChange={handleCheckboxChange}
                           color={'default'}
                           labelKey={'operator.equalizationTimeCalculation.label'}
                           value={'equalizationTimeCalculation'}
                           defaultChecked={defaultValues.equalizationTimeCalculation}
                           checked={editValues.equalizationTimeCalculation}
                           disabled={isSaving}
                           marginTop={0}
                           marginLeft={0}
                           fullWidth={false}
                        />
                     </div>
                     {getValue('equalizationTimeCalculation') && (
                        <EqualizationTimeTable ref={equalizationTimeTableRef} operatorId={operatorId}
                           onChange={() => setIsChanged(true)} />
                     )}
                  </FormCard>
               )}

               {/* Tab: Zones & Segments */}
               {selectedTab === TABS.zonesSegments && operatorId && (
                  <Box sx={{ p: 2, flex: 1, overflowY: 'auto' }}>
                     <ZoneSegmentsTable operatorId={operatorId} />
                  </Box>
               )}

               {/* Tab: Devices (device list with gauge calibrations below) */}
               {selectedTab === TABS.devices && operatorId && (
                  <Box sx={{ p: 2, flex: 1, overflowY: 'auto' }}>
                     <FHGTypography variant={'h6'} style={{ marginBottom: 8 }}>Devices</FHGTypography>
                     <GaugeDevicesTable ref={gaugeDevicesTableRef} operatorId={operatorId} />
                     <Divider style={{ margin: '24px 0 16px' }} />
                     <FHGTypography variant={'h6'} style={{ marginBottom: 8 }}>Gauge Calibrations</FHGTypography>
                     <GaugeCalibrationTable ref={gaugeCalibrationTableRef} operatorId={operatorId} />
                  </Box>
               )}

               {/* Tab: FAQ */}
               {selectedTab === TABS.faq && operatorId && getValue('showFaqs') && (
                  <Box sx={{ p: 2, flex: 1, overflowY: 'auto' }}>
                     <FaqTable ref={faqTableRef} operatorId={operatorId} />
                  </Box>
               )}
            </Paper>
         </Grid>
      </DocumentTitle>
   );
}